import { useCallback, useRef, useState } from "react";
import {
  ChevronContainerIcon,
  FooterSection,
  InnerLinksContainer,
  LandingFooterStyle,
  LandingFooterWrap,
  LinksContainer,
} from "./LandingFooter.style";

import { ChevronUpIcon } from "@tm/static/icons";

import {
  AnchorTag,
  FlexComp,
  ImageComponent,
  ImageTag,
  ParaTag,
  SpanTag,
} from "@tm/static";

import { getAnalytics } from "@tm/insurance/analytics";
import { useBreakpointV2 } from "@tm/ui-widgets";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import { theme } from "@tm/utility/global";

interface footerDataProps {
  id: string;
  label: string;
  listPoints: {
    id: string;
    label: string;
    link?: string;
  }[];
  icon: string;
}

interface LandingFooterProps {
  footerData: footerDataProps[];

  followUsText?: string;
  downloadText?: string;
  locationText?: string;
  countryText?: string;
  mmtText?: string;
  mmtIcon?: string;
  downIcon?: string;
  footerHeartIcon?: string;
  footerHeading?: {
    type?: string;
    value?: string;
  };
  footerCopyrightData?: {
    type?: string;
    items?: [
      {
        type?: string;
        value?: string;
        links?: any;
      }
    ];
  };
  footerTheme?: "dark" | "light";
}

const LandingFooter = ({
  footerData,
  footerHeading,
  downIcon,
  footerCopyrightData,
  footerTheme = "dark",
}: LandingFooterProps) => {
  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);

  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    if (!hasTracked.current) {
      analytics?.trackLandingEvents({
        actionType: "footer_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: footerRef } = useIntersectionObserver(handleIntersection, {
    threshold: 0.5, // Trigger when 50% of component is visible
    triggerOnLeaveViewport: true,
  });

  if (footerData.length === 0 && !footerHeading && !footerCopyrightData) {
    return null;
  }

  const device = useBreakpointV2();
  const [activeIdx, setActiveIdx] = useState(-1);

  let footerTextOne = "";
  let headerAlt = "";
  let headerImg = "";
  let footerTextTwo = "";

  const regex = /^(.*?)!\[(.*?)\]\((.*?)\)(.*?)$/;

  const match = footerHeading?.value?.match(regex);

  if (match) {
    footerTextOne = match[1].trim();
    headerAlt = match[2].trim();
    headerImg = match[3].trim();
    footerTextTwo = match[4].trim();
  }
  const copyrightText = footerCopyrightData?.items?.find(
    (item) => item.type === "copyright"
  )?.value;

  const optionsHandler = (index: number) => {
    if (activeIdx === index) {
      setActiveIdx(-1);
      return;
    }
    setActiveIdx(index);
  };

  return (
    <LandingFooterStyle ref={footerRef} footerTheme={footerTheme}>
      <LandingFooterWrap>
        <FooterSection>
          {footerHeading && (
            <FlexComp
              alignItems="center"
              justifyContent={device === "sm" ? "center" : undefined}
              margin={"0px 0px 50px 0px"}
            >
              {footerTextOne && (
                <SpanTag
                  fontSize={device == "lg" ? "sm" : "xxs"}
                  margin={"0px 8px 4px 0px"}
                  color={footerTheme === "light" ? theme.blackText : "white"}
                >
                  {footerTextOne}
                </SpanTag>
              )}
              {headerImg ? (
                <ImageComponent
                  width="85px"
                  height="27px"
                  src={headerImg}
                  alt={headerAlt}
                />
              ) : null}
              {footerTextTwo && (
                <SpanTag
                  fontSize={device == "lg" ? "sm" : "xxs"}
                  margin={"0px 0px 4px 8px"}
                  color={footerTheme === "light" ? theme.blackText : "white"}
                >
                  {footerTextTwo}
                </SpanTag>
              )}
            </FlexComp>
          )}
          <LinksContainer isCopyrightText={!!copyrightText}>
            {footerData?.map((item, idx) => {
              return (
                item?.label && (
                  <li
                    key={item.id}
                    onClick={
                      device === "sm" ? () => optionsHandler(idx) : undefined
                    }
                  >
                    <FlexComp
                      justifyContent={
                        device === "sm" ? "space-between" : undefined
                      }
                      gap={"8px"}
                      alignItems="center"
                    >
                      <FlexComp gap="10px">
                        {item?.icon && (
                          <ImageComponent
                            width="18px"
                            height="18px"
                            src={item.icon}
                          />
                        )}
                        <ParaTag
                          color={
                            footerTheme === "light" ? theme.blackText : "white"
                          }
                          fontWeight="black"
                          fontSize="xxs"
                          lineHeight="10px"
                          textTransform="uppercase"
                        >
                          {item.label}
                        </ParaTag>
                      </FlexComp>
                      {device === "sm" && (
                        <ChevronContainerIcon isInvert={activeIdx !== idx}>
                          {downIcon ? (
                            <ImageTag src={downIcon} />
                          ) : (
                            <ChevronUpIcon />
                          )}
                        </ChevronContainerIcon>
                      )}
                    </FlexComp>
                    {device === "lg" ? (
                      <InnerLinksContainer footerTheme={footerTheme}>
                        {item?.listPoints?.map((point, index) => (
                          <>
                            <AnchorTag
                              key={point.id}
                              color={
                                footerTheme === "light"
                                  ? theme.greyText
                                  : "white"
                              }
                              fontSize="xs"
                              lineHeight="14px"
                              hrefProp={point.link || "#"}
                            >
                              {point.label}
                              {index < item.listPoints.length - 1 && (
                                <SpanTag
                                  color={
                                    footerTheme === "light"
                                      ? theme.greyText
                                      : "white"
                                  }
                                  fontSize="xs"
                                >
                                  ,&nbsp;
                                </SpanTag>
                              )}
                            </AnchorTag>
                          </>
                        ))}
                      </InnerLinksContainer>
                    ) : (
                      activeIdx === idx && (
                        <InnerLinksContainer>
                          {item.listPoints.map((point, index) => (
                            <>
                              <AnchorTag
                                key={point.id}
                                color="#fff"
                                fontSize="xs"
                                lineHeight={"14px"}
                                hrefProp={point.link || "#"}
                              >
                                {point.label}
                              </AnchorTag>
                              {index < item.listPoints.length - 1 && (
                                <SpanTag color="#fff" fontSize="xs">
                                  ,&nbsp;
                                </SpanTag>
                              )}
                            </>
                          ))}
                        </InnerLinksContainer>
                      )
                    )}
                  </li>
                )
              );
            })}
          </LinksContainer>
        </FooterSection>
      </LandingFooterWrap>
    </LandingFooterStyle>
  );
};

export default LandingFooter;
